import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';

import { useAuth } from '../../auth';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = () => {
    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    setError('');

    // Simulate a brief async delay for realism
    setTimeout(() => {
      const matched = login(email, password);
      setLoading(false);
      if (matched) {
        navigate(
          matched.role === 'Customer' ? '/portal/tickets' : '/',
          { replace: true },
        );
      } else {
        setError('Invalid email or password.');
      }
    }, 400);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#fff',
      '& fieldset': { borderColor: '#D0D5DD' },
      '&:hover fieldset': { borderColor: '#009688' },
      '&.Mui-focused fieldset': { borderColor: '#009688' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#009688' },
  };

  return (
    <Box
      sx={{ minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      {/* Brand heading */}
      <Typography
        sx={{ fontSize: '2rem',
          fontWeight: 800,
          color: '#121926',
          mb: 3,
          letterSpacing: '-0.02em',
        }}
      >
        CoverIT
      </Typography>

      {/* Card */}
      <Box
        sx={{ width: '100%',
          maxWidth: 440,
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          p: '36px',
        }}
      >
        {/* Title */}
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#121926', mb: '6px' }}>
          Sign In
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: '#697586', mb: 3 }}>
          Please enter your sign in details below.
        </Typography>

        {/* Email field */}
        <TextField
          label="Email Address"
          type="email"
          fullWidth
          size="small"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); }}
          onKeyDown={handleKeyDown}
          sx={{ ...fieldSx, mb: 2 }}
          autoComplete="email"
          autoFocus
        />

        {/* Password field */}
        <TextField
          label="Password"
          type="password"
          fullWidth
          size="small"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(''); }}
          onKeyDown={handleKeyDown}
          sx={{ ...fieldSx, mb: error ? 1.5 : 3 }}
          autoComplete="current-password"
        />

        {/* Error message */}
        {error && (
          <Typography sx={{ fontSize: '0.8125rem', color: '#C62828', mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Sign In button — dark navy to match design reference */}
        <Button
          variant="contained"
          fullWidth
          disabled={loading}
          onClick={handleSubmit}
          sx={{ backgroundColor: '#121926',
            color: '#fff',
            '&:hover': { backgroundColor: '#1e2a3a' },
            '&.Mui-disabled': { backgroundColor: '#697586', color: '#c8d0da' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            py: 1.25,
            borderRadius: '8px',
            boxShadow: 'none',
            mb: 2,
          }}
        >
          {loading ? 'Signing in…' : 'Sign In'}
        </Button>

        {/* Reset password (non-functional for POC) */}
        <Box sx={{ textAlign: 'center' }}>
          <Tooltip title="Contact your administrator to reset your password." arrow placement="bottom">
            <Typography
              sx={{ fontSize: '0.875rem',
                color: '#697586',
                cursor: 'default',
                display: 'inline',
                '&:hover': { color: '#121926' },
              }}
            >
              Reset Password
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
